import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { Horse } from '../../../models/horse';
import { SmsAbstractAppPage } from '../../sms-abstract-app-page';

const HORSE_COLORS: { label: string }[] = [
  { label: 'Bay (includes Dark Bay)' },
  { label: 'Black' },
  { label: 'Brown' },
  { label: 'Chestnut (includes Liver, Sorrel)' },
  { label: 'Grey (or Gray)' },
  { label: 'Palomino' },
  { label: 'Buckskin' },
  { label: 'Dun (includes Grulla)' },
  { label: 'Roan (Blue, Red, or Bay Roan)' },
  { label: 'Pinto (includes Piebald, Skewbald, Paint)' },
  { label: 'Appaloosa' },
  { label: 'Cremello / Perlino' },
  { label: 'White' },
];

const HORSE_BREEDS: { code: string; name: string }[] = [
  { code: 'ASB',   name: 'American Saddlebred' },
  { code: 'PRE',   name: 'Andalusian' },
  { code: 'AA',    name: 'Anglo-Arabian' },
  { code: 'APP',   name: 'Appaloosa' },
  { code: 'AR',    name: 'Arabian' },
  { code: 'BAVAR', name: 'Bavarian Warmblood' },
  { code: 'BELG',  name: 'Belgian Draft' },
  { code: 'BWP',   name: 'Belgian Warmblood' },
  { code: 'BWB',   name: 'British Warmblood' },
  { code: 'CLYD',  name: 'Clydesdale' },
  { code: 'CONN',  name: 'Connemara' },
  { code: 'X',     name: 'Crossbred' },
  { code: 'DWB',   name: 'Danish Warmblood' },
  { code: 'KWPN',  name: 'Dutch Warmblood' },
  { code: 'FRIE',  name: 'Friesian' },
  { code: 'GRP',   name: 'German Riding Pony' },
  { code: 'GRAD',  name: 'Grade' },
  { code: 'HACK',  name: 'Hackney Horse' },
  { code: 'HAFL',  name: 'Haflinger' },
  { code: 'HANO',  name: 'Hanoverian' },
  { code: 'HOL',   name: 'Holsteiner' },
  { code: 'ID',    name: 'Irish Draught' },
  { code: 'ISH',   name: 'Irish Sport Horse' },
  { code: 'LUS',   name: 'Lusitano' },
  { code: 'MOR',   name: 'Morgan' },
  { code: 'NFP',   name: 'New Forest Pony' },
  { code: 'OLD',   name: 'Oldenburg' },
  { code: 'APH',   name: 'Paint Horse' },
  { code: 'PASO',  name: 'Paso Fino' },
  { code: 'PERC',  name: 'Percheron' },
  { code: 'POA',   name: 'Pony of the Americas' },
  { code: 'QH',    name: 'Quarter Horse' },
  { code: 'RHIN',  name: 'Rhinelander' },
  { code: 'SF',    name: 'Selle Francais' },
  { code: 'SHET',  name: 'Shetland' },
  { code: 'SHIR',  name: 'Shire' },
  { code: 'STB',   name: 'Standardbred' },
  { code: 'SWB',   name: 'Swedish Warmblood' },
  { code: 'TB',    name: 'Thoroughbred' },
  { code: 'TRAK',  name: 'Trakehner' },
  { code: 'WELS',  name: 'Welsh Pony' },
  { code: 'WESTF', name: 'Westphalian' },
  { code: 'ZANG',  name: 'Zangersheide' },
];

const IOC_COUNTRIES: { code: string; name: string }[] = [
  { code: 'AFG', name: 'Afghanistan' },
  { code: 'ALB', name: 'Albania' },
  { code: 'ALG', name: 'Algeria' },
  { code: 'AND', name: 'Andorra' },
  { code: 'ANG', name: 'Angola' },
  { code: 'ANT', name: 'Antigua and Barbuda' },
  { code: 'ARG', name: 'Argentina' },
  { code: 'ARM', name: 'Armenia' },
  { code: 'ARU', name: 'Aruba' },
  { code: 'ASA', name: 'American Samoa' },
  { code: 'AUS', name: 'Australia' },
  { code: 'AUT', name: 'Austria' },
  { code: 'AZE', name: 'Azerbaijan' },
  { code: 'BAH', name: 'Bahamas' },
  { code: 'BAN', name: 'Bangladesh' },
  { code: 'BAR', name: 'Barbados' },
  { code: 'BDI', name: 'Burundi' },
  { code: 'BEL', name: 'Belgium' },
  { code: 'BEN', name: 'Benin' },
  { code: 'BER', name: 'Bermuda' },
  { code: 'BHU', name: 'Bhutan' },
  { code: 'BIH', name: 'Bosnia and Herzegovina' },
  { code: 'BIZ', name: 'Belize' },
  { code: 'BLR', name: 'Belarus' },
  { code: 'BOL', name: 'Bolivia' },
  { code: 'BOT', name: 'Botswana' },
  { code: 'BRA', name: 'Brazil' },
  { code: 'BRN', name: 'Bahrain' },
  { code: 'BRU', name: 'Brunei' },
  { code: 'BUL', name: 'Bulgaria' },
  { code: 'BUR', name: 'Burkina Faso' },
  { code: 'CAF', name: 'Central African Republic' },
  { code: 'CAM', name: 'Cambodia' },
  { code: 'CAN', name: 'Canada' },
  { code: 'CAY', name: 'Cayman Islands' },
  { code: 'CGO', name: 'Congo' },
  { code: 'CHA', name: 'Chad' },
  { code: 'CHI', name: 'Chile' },
  { code: 'CHN', name: 'China' },
  { code: 'CIV', name: "Côte d'Ivoire" },
  { code: 'CMR', name: 'Cameroon' },
  { code: 'COD', name: 'DR Congo' },
  { code: 'COK', name: 'Cook Islands' },
  { code: 'COL', name: 'Colombia' },
  { code: 'COM', name: 'Comoros' },
  { code: 'CPV', name: 'Cape Verde' },
  { code: 'CRC', name: 'Costa Rica' },
  { code: 'CRO', name: 'Croatia' },
  { code: 'CUB', name: 'Cuba' },
  { code: 'CYP', name: 'Cyprus' },
  { code: 'CZE', name: 'Czech Republic' },
  { code: 'DEN', name: 'Denmark' },
  { code: 'DJI', name: 'Djibouti' },
  { code: 'DMA', name: 'Dominica' },
  { code: 'DOM', name: 'Dominican Republic' },
  { code: 'ECU', name: 'Ecuador' },
  { code: 'EGY', name: 'Egypt' },
  { code: 'ERI', name: 'Eritrea' },
  { code: 'ESA', name: 'El Salvador' },
  { code: 'ESP', name: 'Spain' },
  { code: 'EST', name: 'Estonia' },
  { code: 'ETH', name: 'Ethiopia' },
  { code: 'FIJ', name: 'Fiji' },
  { code: 'FIN', name: 'Finland' },
  { code: 'FRA', name: 'France' },
  { code: 'GAB', name: 'Gabon' },
  { code: 'GAM', name: 'Gambia' },
  { code: 'GBR', name: 'Great Britain' },
  { code: 'GBS', name: 'Guinea-Bissau' },
  { code: 'GEO', name: 'Georgia' },
  { code: 'GEQ', name: 'Equatorial Guinea' },
  { code: 'GER', name: 'Germany' },
  { code: 'GHA', name: 'Ghana' },
  { code: 'GRE', name: 'Greece' },
  { code: 'GRN', name: 'Grenada' },
  { code: 'GUA', name: 'Guatemala' },
  { code: 'GUI', name: 'Guinea' },
  { code: 'GUM', name: 'Guam' },
  { code: 'GUY', name: 'Guyana' },
  { code: 'HAI', name: 'Haiti' },
  { code: 'HKG', name: 'Hong Kong' },
  { code: 'HON', name: 'Honduras' },
  { code: 'HUN', name: 'Hungary' },
  { code: 'INA', name: 'Indonesia' },
  { code: 'IND', name: 'India' },
  { code: 'IRI', name: 'Iran' },
  { code: 'IRL', name: 'Ireland' },
  { code: 'IRQ', name: 'Iraq' },
  { code: 'ISL', name: 'Iceland' },
  { code: 'ISR', name: 'Israel' },
  { code: 'ISV', name: 'US Virgin Islands' },
  { code: 'ITA', name: 'Italy' },
  { code: 'IVB', name: 'British Virgin Islands' },
  { code: 'JAM', name: 'Jamaica' },
  { code: 'JOR', name: 'Jordan' },
  { code: 'JPN', name: 'Japan' },
  { code: 'KAZ', name: 'Kazakhstan' },
  { code: 'KEN', name: 'Kenya' },
  { code: 'KGZ', name: 'Kyrgyzstan' },
  { code: 'KOR', name: 'South Korea' },
  { code: 'KOS', name: 'Kosovo' },
  { code: 'KUW', name: 'Kuwait' },
  { code: 'LAO', name: 'Laos' },
  { code: 'LAT', name: 'Latvia' },
  { code: 'LBA', name: 'Libya' },
  { code: 'LBR', name: 'Liberia' },
  { code: 'LCA', name: 'Saint Lucia' },
  { code: 'LES', name: 'Lesotho' },
  { code: 'LIB', name: 'Lebanon' },
  { code: 'LIE', name: 'Liechtenstein' },
  { code: 'LTU', name: 'Lithuania' },
  { code: 'LUX', name: 'Luxembourg' },
  { code: 'MAD', name: 'Madagascar' },
  { code: 'MAR', name: 'Morocco' },
  { code: 'MAS', name: 'Malaysia' },
  { code: 'MAW', name: 'Malawi' },
  { code: 'MDA', name: 'Moldova' },
  { code: 'MDV', name: 'Maldives' },
  { code: 'MEX', name: 'Mexico' },
  { code: 'MGL', name: 'Mongolia' },
  { code: 'MKD', name: 'North Macedonia' },
  { code: 'MLI', name: 'Mali' },
  { code: 'MLT', name: 'Malta' },
  { code: 'MNE', name: 'Montenegro' },
  { code: 'MON', name: 'Monaco' },
  { code: 'MOZ', name: 'Mozambique' },
  { code: 'MRI', name: 'Mauritius' },
  { code: 'MTN', name: 'Mauritania' },
  { code: 'MYA', name: 'Myanmar' },
  { code: 'NAM', name: 'Namibia' },
  { code: 'NCA', name: 'Nicaragua' },
  { code: 'NED', name: 'Netherlands' },
  { code: 'NEP', name: 'Nepal' },
  { code: 'NGR', name: 'Nigeria' },
  { code: 'NIG', name: 'Niger' },
  { code: 'NOR', name: 'Norway' },
  { code: 'NRU', name: 'Nauru' },
  { code: 'NZL', name: 'New Zealand' },
  { code: 'OMA', name: 'Oman' },
  { code: 'PAK', name: 'Pakistan' },
  { code: 'PAN', name: 'Panama' },
  { code: 'PAR', name: 'Paraguay' },
  { code: 'PER', name: 'Peru' },
  { code: 'PHI', name: 'Philippines' },
  { code: 'PLE', name: 'Palestine' },
  { code: 'PLW', name: 'Palau' },
  { code: 'PNG', name: 'Papua New Guinea' },
  { code: 'POL', name: 'Poland' },
  { code: 'POR', name: 'Portugal' },
  { code: 'PRK', name: 'North Korea' },
  { code: 'PUR', name: 'Puerto Rico' },
  { code: 'QAT', name: 'Qatar' },
  { code: 'ROU', name: 'Romania' },
  { code: 'RSA', name: 'South Africa' },
  { code: 'RUS', name: 'Russia' },
  { code: 'RWA', name: 'Rwanda' },
  { code: 'SAM', name: 'Samoa' },
  { code: 'SEN', name: 'Senegal' },
  { code: 'SEY', name: 'Seychelles' },
  { code: 'SGP', name: 'Singapore' },
  { code: 'SKN', name: 'Saint Kitts and Nevis' },
  { code: 'SLE', name: 'Sierra Leone' },
  { code: 'SLO', name: 'Slovenia' },
  { code: 'SMR', name: 'San Marino' },
  { code: 'SOL', name: 'Solomon Islands' },
  { code: 'SOM', name: 'Somalia' },
  { code: 'SRB', name: 'Serbia' },
  { code: 'SRI', name: 'Sri Lanka' },
  { code: 'SSD', name: 'South Sudan' },
  { code: 'STP', name: 'São Tomé and Príncipe' },
  { code: 'SUD', name: 'Sudan' },
  { code: 'SUI', name: 'Switzerland' },
  { code: 'SUR', name: 'Suriname' },
  { code: 'SVK', name: 'Slovakia' },
  { code: 'SWE', name: 'Sweden' },
  { code: 'SWZ', name: 'Eswatini' },
  { code: 'SYR', name: 'Syria' },
  { code: 'TAN', name: 'Tanzania' },
  { code: 'TGA', name: 'Tonga' },
  { code: 'THA', name: 'Thailand' },
  { code: 'TJK', name: 'Tajikistan' },
  { code: 'TKM', name: 'Turkmenistan' },
  { code: 'TLS', name: 'Timor-Leste' },
  { code: 'TOG', name: 'Togo' },
  { code: 'TPE', name: 'Chinese Taipei' },
  { code: 'TTO', name: 'Trinidad and Tobago' },
  { code: 'TUN', name: 'Tunisia' },
  { code: 'TUR', name: 'Turkey' },
  { code: 'TUV', name: 'Tuvalu' },
  { code: 'UAE', name: 'United Arab Emirates' },
  { code: 'UGA', name: 'Uganda' },
  { code: 'UKR', name: 'Ukraine' },
  { code: 'URU', name: 'Uruguay' },
  { code: 'USA', name: 'United States' },
  { code: 'UZB', name: 'Uzbekistan' },
  { code: 'VAN', name: 'Vanuatu' },
  { code: 'VEN', name: 'Venezuela' },
  { code: 'VIE', name: 'Vietnam' },
  { code: 'VIN', name: 'Saint Vincent and the Grenadines' },
  { code: 'YEM', name: 'Yemen' },
  { code: 'ZAM', name: 'Zambia' },
  { code: 'ZIM', name: 'Zimbabwe' },
];

const SEX_OPTIONS = ['Gelding', 'Mare', 'Stallion'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

@Component({
  standalone: false,
  selector: 'edit-horse',
  inputs: ['horse'],
  templateUrl: './edit-horse.component.html',
  styleUrls: ['./edit-horse.component.css']
})
export class EditHorseComponent extends SmsAbstractAppPage implements OnInit, OnChanges {
  @Input() horse: Horse;
  @Output() horse_saved$: EventEmitter<Horse> = new EventEmitter<Horse>();

  readonly HORSE_COLORS = HORSE_COLORS;
  readonly HORSE_BREEDS = HORSE_BREEDS;
  readonly IOC_COUNTRIES = IOC_COUNTRIES;
  readonly SEX_OPTIONS = SEX_OPTIONS;
  readonly MONTHS = MONTHS;

  breed_display: string = '';
  birth_place_display: string = '';
  passport_country_display: string = '';
  dob_year: number | null = null;
  dob_month: number | null = null;
  dob_day: number | null = null;
  height_value: string = '';
  height_unit: 'HH' | 'cm' | '' = '';
  sex_warning: boolean = false;

  constructor() { super(); }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['horse'] && this.horse) {
      this.initFormState();
    }
  }

  private initFormState() {
    this.initBreedDisplay();
    this.initBirthPlaceDisplay();
    this.initPassportCountryDisplay();
    this.initDob();
    this.initHeight();
    this.checkSexWarning();
  }

  private initBreedDisplay() {
    const match = HORSE_BREEDS.find(b => b.code === this.horse.breed);
    this.breed_display = match ? match.name : (this.horse.breed || '');
  }

  private initBirthPlaceDisplay() {
    const match = IOC_COUNTRIES.find(c => c.code === this.horse.birth_place);
    this.birth_place_display = match ? match.name : (this.horse.birth_place || '');
  }

  private initPassportCountryDisplay() {
    const match = IOC_COUNTRIES.find(c => c.code === this.horse.passport_country);
    this.passport_country_display = match ? match.name : (this.horse.passport_country || '');
  }

  private initDob() {
    const raw = (this.horse.date_of_birth as any) as string;
    this.dob_year = null;
    this.dob_month = null;
    this.dob_day = null;
    if (!raw) return;
    const parts = String(raw).split('T')[0].split('-');
    if (parts.length === 3) {
      this.dob_year = parseInt(parts[0], 10);
      this.dob_month = parseInt(parts[1], 10);
      this.dob_day = parseInt(parts[2], 10);
    }
  }

  private initHeight() {
    const h = this.horse.height || '';
    const m = h.match(/^(.+?)\s+(HH|hh|cm|CM)$/);
    if (m) {
      this.height_value = m[1].trim();
      this.height_unit = /^hh$/i.test(m[2]) ? 'HH' : 'cm';
    } else {
      this.height_value = h;
      this.height_unit = '';
    }
  }

  private checkSexWarning() {
    this.sex_warning = !!(this.horse.sex && !SEX_OPTIONS.includes(this.horse.sex));
  }

  handleColorCommit() {
    this.horse.color = (this.horse.color || '').replace(/\s*\([^)]*\)/g, '').trim();
  }

  handleBreedChange() {
    const match = HORSE_BREEDS.find(b => b.name === this.breed_display);
    this.horse.breed = match ? match.code : this.breed_display;
  }

  handleBirthPlaceChange() {
    const match = IOC_COUNTRIES.find(c => c.name === this.birth_place_display);
    this.horse.birth_place = match ? match.code : this.birth_place_display;
  }

  handlePassportCountryChange() {
    const match = IOC_COUNTRIES.find(c => c.name === this.passport_country_display);
    this.horse.passport_country = match ? match.code : this.passport_country_display;
  }

  handleSexInput() {
    this.sex_warning = !!(this.horse.sex && !SEX_OPTIONS.includes(this.horse.sex));
  }

  quickSetSex(value: string) {
    this.horse.sex = value;
    this.sex_warning = false;
  }

  setHeightUnit(unit: 'HH' | 'cm') {
    this.height_unit = unit;
    this.syncHeight();
  }

  syncHeight() {
    if (this.height_unit) {
      this.horse.height = this.height_value ? `${this.height_value} ${this.height_unit}` : '';
    } else {
      this.horse.height = this.height_value;
    }
  }

  updateDob() {
    if (this.dob_year && this.dob_month && this.dob_day) {
      const m = String(this.dob_month).padStart(2, '0');
      const d = String(this.dob_day).padStart(2, '0');
      (this.horse as any).date_of_birth = `${this.dob_year}-${m}-${d}`;
    }
  }

  get dob_years(): number[] {
    const cur = new Date().getFullYear();
    return Array.from({ length: 34 }, (_, i) => cur - 1 - i);
  }

  get dob_days(): number[] {
    const count = (this.dob_year && this.dob_month)
      ? new Date(this.dob_year, this.dob_month, 0).getDate()
      : 31;
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  handleSaveHorse(horse: Horse) {
    this.horse_saved$.emit(horse);
  }
}
